/**
 * O histórico do caso (E137.F1/F3, GUS-160, ADR-0093). Espelha `apps/api/types/trilha_do_caso.py`.
 *
 * - `GET /casos/:id/historico`: os registros em ordem e a verificação da trilha.
 * - `POST /casos/:id/historico/eventos/:eid/correcao`: a correção como registro novo, ligado ao
 *   anterior, que fica como foi gravado.
 *
 * É da equipe: o banco recebe 403.
 */
import axios, { type AxiosInstance } from 'axios';

export type TipoDoEvento =
  | 'recebimento_selado'
  | 'conferencia_registrada'
  | 'ocorrencia_aberta'
  | 'ocorrencia_resolvida'
  | 'exame_verificado'
  | 'evento_corrigido'
  | 'laudo_editado'
  | 'narrativa_gerada'
  | 'narrativa_aprovada'
  | 'narrativa_reaberta'
  | 'pre_analise_executada'
  | 'documento_emitido'
  | 'laudo_assinado'
  | 'documento_conferido'
  | 'documento_divergente'
  | 'ficha_da_leitura_gravada'
  | 'leitura_conferida'
  | 'leitura_retomada'
  | 'limitacao_da_leitura_registrada'
  | 'fotografia_recortada'
  | 'pedido_atualizado'
  | 'acesso_por_qr_emitido'
  | 'acesso_por_qr_revogado'
  | 'arquivo_recusado'
  | 'etapa_conferida'
  | 'etapa_retomada'
  | 'limitacao_da_etapa_registrada'
  | 'assinatura_reconciliada'
  | 'mensagem_do_caso'
  | 'leitura_revista'
  | 'envio_vinculado'
  | 'certificacao_revogada'
  | 'origem_informada'
  | 'ccb_divergente_mantida'
  | 'analise_morfologica_refeita'
  | 'dado_revisto'
  | 'documento_recortado'
  | 'documento_excluido';

export interface EventoNoHistoricoData {
  id: string;
  seq: number;
  tipo: TipoDoEvento;
  instante: string;
  ator: 'usuario' | 'rotina';
  quem: string | null;
  objeto_tipo: string | null;
  objeto_id: string | null;
  dados: Record<string, unknown>;
  corrige_evento_id: string | null;
  hash: string;
  /** E170.F1.T06: a forma do "Laudo assinado" (registro na plataforma ou ICP-Brasil). */
  forma_da_assinatura?: 'interna_registrada' | 'icp_brasil' | null;
  /**
   * E209.F3.T05 (GUS-296 item 7b): o nome de exibição de quem agiu ("Preparação automática" na conta
   * do sistema); a tela o mostra e deixa o e-mail (`quem`) na dica.
   */
  quem_nome?: string | null;
}

export type ProblemaDaTrilha = 'alterado' | 'removido' | 'fora_de_ordem';

export interface VerificacaoDaTrilhaData {
  situacao: 'integra' | 'violada' | 'vazia';
  eventos: number;
  problemas: { seq: number; problema: ProblemaDaTrilha }[];
}

export interface HistoricoDoCasoData {
  case_id: string;
  eventos: EventoNoHistoricoData[];
  verificacao: VerificacaoDaTrilhaData;
}

export type MotivoDaFalhaDoHistorico = 'sem_permissao' | 'nao_encontrado' | 'indisponivel';

export class HistoricoDoCasoError extends Error {
  constructor(readonly motivo: MotivoDaFalhaDoHistorico) {
    super(motivo);
    this.name = 'HistoricoDoCasoError';
  }
}

const HTTP_FORBIDDEN = 403;
const HTTP_NOT_FOUND = 404;

function motivoDoErro(err: unknown): MotivoDaFalhaDoHistorico {
  if (!axios.isAxiosError(err)) return 'indisponivel';
  if (err.response?.status === HTTP_FORBIDDEN) return 'sem_permissao';
  if (err.response?.status === HTTP_NOT_FOUND) return 'nao_encontrado';
  return 'indisponivel';
}

export async function lerHistoricoDoCaso(
  client: AxiosInstance,
  caseId: string,
): Promise<HistoricoDoCasoData> {
  try {
    const { data } = await client.get<HistoricoDoCasoData>(`/casos/${caseId}/historico`);
    return data;
  } catch (err) {
    throw new HistoricoDoCasoError(motivoDoErro(err));
  }
}

export async function corrigirEvento(
  client: AxiosInstance,
  caseId: string,
  eventoId: string,
  motivo: string,
): Promise<string> {
  try {
    const { data } = await client.post<{ id: string }>(
      `/casos/${caseId}/historico/eventos/${eventoId}/correcao`,
      { motivo },
    );
    return data.id;
  } catch (err) {
    throw new HistoricoDoCasoError(motivoDoErro(err));
  }
}
